import type {
  AgentFocus,
  AgentScreen,
  AgentSurface,
  AskDashboardContext,
  AskGrowthContext,
  OpsCommandPerformancePayload,
  PerfSettings,
} from '../types';
import { frameLabel } from './perfSettings';

function chromePeriodFields(
  perfSettings: PerfSettings,
  perf?: OpsCommandPerformancePayload | null,
): Omit<AskDashboardContext, 'mode'> {
  const start = perf?.periodStart ?? perf?.dateRange?.start ?? perfSettings.dateStart ?? undefined;
  const end = perf?.periodEnd ?? perf?.dateRange?.end ?? perfSettings.dateEnd ?? undefined;
  return {
    ...(start && end ? { serviceDateStart: start, serviceDateEnd: end } : {}),
    compareServiceDateStart: perf?.comparePeriod?.start,
    compareServiceDateEnd: perf?.comparePeriod?.end,
    comparePeriodLabel: perf?.comparePeriod?.label,
    periodLabel: perf?.periodLabel,
    frameLabel: perf?.frameLabel ?? frameLabel(perfSettings),
    aggregation: perfSettings.aggregation,
    frameCount: perfSettings.frameCount,
    frameUnit: perfSettings.frameUnit,
    ...(perfSettings.dateStart && perfSettings.dateEnd
      ? { dateStart: perfSettings.dateStart, dateEnd: perfSettings.dateEnd }
      : {}),
  };
}

/** Growth's own period and comparison, which differ from Performance's:
 *  Growth stops at yesterday and compares with the same weeks last year by
 *  weekday unless the person chose otherwise (WP3.1, D4). */
function growthPeriodFields(growth: AskGrowthContext): Partial<AskDashboardContext> {
  return {
    ...(growth.period
      ? {
          serviceDateStart: growth.period.start,
          serviceDateEnd: growth.period.end,
          periodLabel: growth.period.label,
        }
      : {}),
    compareServiceDateStart: growth.compare?.start ?? undefined,
    compareServiceDateEnd: growth.compare?.end ?? undefined,
    comparePeriodLabel: growth.compare?.label ?? undefined,
  };
}

/** Every assistant question: the Performance chrome window plus where the
 *  operator is (size, screen) and what they asked about (docs/18 §5). On
 *  Growth, Growth's window and the figures the screen shows (WP4.5), so the
 *  answer is about the same online reservations, not all reservations. */
export function buildAssistantContext(
  perfSettings: PerfSettings,
  perf: OpsCommandPerformancePayload | null | undefined,
  where: {
    surface: AgentSurface;
    screen?: AgentScreen;
    focus?: AgentFocus | null;
    growth?: AskGrowthContext | null;
  },
): AskDashboardContext {
  const growth = where.screen === 'growth' ? where.growth : null;
  return {
    ...chromePeriodFields(perfSettings, perf),
    ...(growth ? growthPeriodFields(growth) : {}),
    mode: 'assistant',
    surface: where.surface,
    ...(where.screen ? { screen: where.screen } : {}),
    ...(where.focus ? { focus: where.focus } : {}),
    ...(growth ? { growth } : {}),
  };
}

/** The North Star flow binds its baseline to the chrome window (last 4 weeks
 *  when the chrome has no explicit dates). */
export function buildNorthStarContext(perfSettings: PerfSettings): AskDashboardContext {
  const end = perfSettings.dateEnd ?? new Date().toISOString().slice(0, 10);
  const start =
    perfSettings.dateStart ??
    new Date(Date.now() - 28 * 86400000).toISOString().slice(0, 10);
  return {
    serviceDateStart: start,
    serviceDateEnd: end,
    frameLabel: `${perfSettings.frameCount} ${perfSettings.frameUnit}(s)`,
    aggregation: perfSettings.aggregation,
    mode: 'north-star',
  };
}
